/**
 * styles.css — Potter's Vineyard tenant-scoped CSS.
 *
 * Injected AFTER src/styles/variables.css by init.js injectSharedStyles(), so these
 * :host(.ws-tenant-pottersvineyard) rules override the base :host vars and the shared
 * rich-card scaffold.
 *
 * Ported verbatim (structure/layout) from Lightning Rock Winery's styles.css —
 * same Commerce7 "reviewed" checkout flow, same card/chip/carousel structure —
 * with ONE deliberate difference: no hardcoded --ws-card-accent override here.
 * That variable defaults to var(--ws-primary) (src/styles/variables.css),
 * which IS Potter's own admin-configured primary_color (#205C48), so every
 * var(--ws-card-accent, ...) rule below automatically renders in Potter's own
 * green instead of Lightning Rock's maroon — no color values were copied.
 */

:host(.ws-tenant-pottersvineyard) {
  --ws-card-title-font: var(--ws-monst, var(--ws-font));
  --ws-card-desc-font: var(--ws-karla, var(--ws-font));
  --ws-drawer-bottom: 86px;
}

/* Production input: Karla text, a 38px light-gray field, and taupe icons. */
:host(.ws-tenant-pottersvineyard) .ws-input-area {
  padding: 8px 10px 0;
  gap: 5px;
}

:host(.ws-tenant-pottersvineyard) .ws-input-wrap {
  height: 38px;
  min-height: 38px;
  box-sizing: border-box;
  padding: 0 5px 0 15px;
  background: rgba(192, 195, 196, 0.18);
}

:host(.ws-tenant-pottersvineyard) .ws-input {
  min-width: 0;
  padding: 8px 0;
  font-family: "Karla", sans-serif !important;
  font-size: 16px;
  font-weight: 600 !important;
  line-height: 20px;
  color: #797264;
}

:host(.ws-tenant-pottersvineyard) .ws-input::placeholder {
  color: #797264;
  opacity: 1;
}

:host(.ws-tenant-pottersvineyard) .ws-btn-mic {
  width: 30px;
  height: 32px;
  padding: 0;
  background: transparent;
}

:host(.ws-tenant-pottersvineyard) .ws-btn-mic svg {
  width: 26px;
  height: 26px;
}

:host(.ws-tenant-pottersvineyard) .ws-btn-mic--listening svg circle {
  fill: var(--ws-mic-active-bg);
}

:host(.ws-tenant-pottersvineyard) .ws-btn-send {
  width: 30px;
  height: 32px;
  padding: 0;
  color: var(--ws-mic-color);
}

:host(.ws-tenant-pottersvineyard) .ws-btn-send svg {
  width: 29px;
  height: 24px;
}

:host(.ws-tenant-pottersvineyard) .ws-header-logo img {
  height: 48px;
  width: auto;
  max-width: 200px;
}

/* ── Header account dropdown (old #login-dropdown-menu) ───────────────────
   Icons are swapped in components/Header.js. Menu: white, 1px #E5E5E5 border,
   8px radius, 6px 4px padding, min 138px, no shadow, opening down and to the
   left of the person icon (old left:-26px from the 22px glyph; our glyph sits
   5px inside a 32px button). */
:host(.ws-tenant-pottersvineyard) .ws-login-menu {
  left: -21px;
  right: auto;
  min-width: 138px;
  padding: 6px 4px;
  gap: 2px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: none;
}
/* Old .dropdown-option: icon tile + label, 8px gap, 14px/600 black, no padding. */
:host(.ws-tenant-pottersvineyard) .ws-login-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: #000;
}
/* Base `#ws-chat-panel button` (ID rule) would keep the Login row on --ws-font. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-login-item {
  font-family: var(--ws-karla);
}
:host(.ws-tenant-pottersvineyard) .ws-login-item:hover {
  background: none;
}
/* Old #logout-option-btn: regular weight. */
:host(.ws-tenant-pottersvineyard) .ws-login-divider + .ws-login-item {
  font-weight: 400;
}
/* Old .avatar-icon-wrapper: 32px #EBEDF0 rounded tile, glyph in header color. */
:host(.ws-tenant-pottersvineyard) .ws-pv-login-icon {
  display: grid;
  place-items: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #ebedf0;
  color: var(--ws-header-action-color);
}
/* Old .user-avatar: logged-in initial on the brand green. */
:host(.ws-tenant-pottersvineyard) .ws-pv-login-icon--user {
  background: var(--ws-primary);
  color: #ffffff;
}

:host(.ws-tenant-pottersvineyard) .ws-banner-img {
  height: auto;
}

/* Promo strip ("Ultra Boutique… | VISIT US") — parity with old prod
   .days-promotion .promotion-text / .promo-text-left-featured /
   .promo-text-right-featured (live pottersvineyard.winespeak.ai index.css). */
:host(.ws-tenant-pottersvineyard) .ws-banner-promo {
  min-height: 41px;
  padding: 10px 12px;
}
:host(.ws-tenant-pottersvineyard) .ws-banner-text {
  max-width: 72%;
  font-family: var(--ws-karla);
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: 0;
  text-align: left;
}
/* Multi-line banner_text (event promo — price split onto its own line by
   formatBannerText in index.js, or "\n" in config): Goosecross layout — bold
   uppercase headline, smaller regular detail lines (date / price). */
:host(.ws-tenant-pottersvineyard) .ws-banner-text:has(span) {
  flex: 1;
  min-width: 0;
  max-width: none;
}
:host(.ws-tenant-pottersvineyard) .ws-banner-text span:first-child {
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
}
:host(.ws-tenant-pottersvineyard) .ws-banner-text span ~ span {
  font-size: 13px;
  font-weight: 400;
}

/* "VISIT US": small white pill (old .promo-text-right-featured). */
:host(.ws-tenant-pottersvineyard) .ws-banner-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  /* Size to the label (no 30% cap): longer event labels like "PURCHASE
     TICKET" overflowed the padding on phone-width panels. The headline
     (flex: 1) wraps into the remaining space instead. */
  max-width: none;
  padding: 12px;
  background: #ffffff;
  color: #8c724d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 90%;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
  flex-shrink: 0;
}
/* Old prod inherited Karla from the winery page body. Base
   `#ws-chat-panel button { font-family: var(--ws-font) }` is an ID rule and
   beats a plain :host() override, so scope through the panel id (same as
   fogartywinery's chips). */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-banner-cta {
  font-family: var(--ws-karla);
}
:host(.ws-tenant-pottersvineyard) .ws-banner-cta:hover {
  background: #f2f2f2;
  opacity: 1;
}

/* Bot + welcome bubble — old prod .bot-message .message-content: Karla 15px/400,
   line-height 1.3em (size/line-height already shared), #1D1D1D on #F7F7F7. */
:host(.ws-tenant-pottersvineyard) :is(.ws-bot-bubble, .ws-welcome) {
  background: #f7f7f7;
  color: #1d1d1d;
  font-family: var(--ws-karla);
}
:host(.ws-tenant-pottersvineyard)
  :is(.ws-bot-bubble, .ws-welcome)
  :is(p, li, strong, b, em, span) {
  font-family: var(--ws-karla);
}

/* Typing indicator — old prod addLoadingAnimationAsBotResponse: a bare
   .dots-loader beside the avatar (no bubble), dark-gray #424245 dots, 30px wide,
   l1 1s steps(4). Shape + animation are already shared (.ws-typing-dots); clear
   the bubble set above and recolor the dots (base uses --ws-accent = brown).
   Padding stays so the dots sit centered against the 24px avatar. */
:host(.ws-tenant-pottersvineyard) .ws-bot-bubble.ws-typing {
  background: none;
  border: none;
  box-shadow: none;
}
:host(.ws-tenant-pottersvineyard) .ws-typing-dots {
  width: 30px;
  background: radial-gradient(circle closest-side, #424245 90%, transparent)
    0 / 33.3333333333% 100% space;
}

/* Guided-flow chip text — old prod .option-button: Karla 15px/500, line-height 16px. */
:host(.ws-tenant-pottersvineyard) .ws-prompt-label {
  font-family: var(--ws-karla);
  font-size: 15px;
  font-weight: 500;
  line-height: 16px;
}

:host(.ws-tenant-pottersvineyard) .ws-rich-review-button {
  border-radius: 1px;
}

:host(.ws-tenant-pottersvineyard) .ws-card-title {
  /* font-family: var(--ws-card-title-font); */
}

/* Tasting cards use a rounded glow shell instead of a hard border (structure
   ported from Lightning Rock's gold-glow tasting card; shadow tint kept
   neutral gray here rather than importing Lightning Rock's gold). */
:host(.ws-tenant-pottersvineyard) .ws-tasting-card {
  border-color: transparent;
  border-radius: 12px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.18);
}

/* Bottom-pin the action row across equal-height carousel cards so CTAs line up
   regardless of how much content sits above them (wine: price+CTA; tasting:
   Learn more + footer as one group; event: footer). */
:host(.ws-tenant-pottersvineyard) .ws-carousel-item.ws-card {
  display: flex;
  flex-direction: column;
  height: auto;
  align-self: stretch;
}
:host(.ws-tenant-pottersvineyard) .ws-carousel-item .ws-card-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
}
/* wine: price + CTA sink together to the card bottom */
:host(.ws-tenant-pottersvineyard) .ws-wine-card .ws-card-price {
  margin-top: auto;
}
/* Tasting: pin Learn more (ss2) — free space ABOVE the button so every card's
   Learn more + price/Reserve row share one horizontal line. Do NOT also put
   margin-top:auto on .ws-tasting-footer (that splits space and misaligns). */
:host(.ws-tenant-pottersvineyard) .ws-tasting-card .ws-card-learnmore {
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-tasting-footer {
  margin-top: 10px;
}
/* Event: no Learn more group — pin the price/CTA footer alone */
:host(.ws-tenant-pottersvineyard) .ws-event-footer {
  margin-top: auto;
}

/* Keep "$xx" + "Per Person" on one line in the event/tasting footer — the long
   uppercase CTA otherwise eats the footer row and squeezes the price block,
   wrapping the unit to two lines. Stop the price block from shrinking +
   nowrap the unit, tighten the gap, and trim the CTA's footprint. */
:host(.ws-tenant-pottersvineyard) .ws-event-footer,
:host(.ws-tenant-pottersvineyard) .ws-tasting-footer {
  gap: 8px;
}
:host(.ws-tenant-pottersvineyard) .ws-event-footer .ws-price-block,
:host(.ws-tenant-pottersvineyard) .ws-tasting-footer .ws-price-block {
  flex-shrink: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-event-footer .ws-card-unit,
:host(.ws-tenant-pottersvineyard) .ws-tasting-footer .ws-card-unit {
  white-space: nowrap;
}
:host(.ws-tenant-pottersvineyard) .ws-event-footer .ws-card-cta,
:host(.ws-tenant-pottersvineyard) .ws-tasting-footer .ws-card-cta {
  padding: 10px 14px;
  font-size: 14px;
}

:host(.ws-tenant-pottersvineyard) .ws-card-cta {
  border-radius: 6px;
}

/* Reserved events ("Weddings, Corporate & Private Events") carry no price, so
   the footer holds only the CTA. With justify-content:space-between a lone
   child sits at the LEFT — push it right so it stays right-aligned. Harmless
   for priced events (already right via space-between). */
:host(.ws-tenant-pottersvineyard) .ws-event-footer .ws-card-cta {
  margin-left: auto;
}

/* ── Wine club card — old prod .carousel-container2 .card-box ─────────────
   Card: no padding, 6px radius, gold glow, Karla. Cover 6px padding (below),
   lithos-pro title, 8px inner box with dark check bullets, and Learn more +
   the price/Join row pinned to the bottom. */
:host(.ws-tenant-pottersvineyard) .ws-membership-card {
  padding: 0;
  border-radius: 6px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  font-family: var(--ws-karla);
}
/* Old .card-box-title: "lithos-pro" 16px/400, 16px line, 8px padding, green
   (from the winery's Adobe kit / theme.font_url; Karla if the kit is absent). */
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-card-title {
  margin: 0;
  padding: 8px;
  font-family: "lithos-pro", var(--ws-karla);
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  color: var(--ws-card-accent);
  white-space: pre-line; /* render the <br>→newline from the title as a break */
}
/* Old .innercardbox — 8px padding; column so Learn more + price/Join pin to the bottom. */
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-rich-inner {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px;
  margin: 0;
  background: transparent;
  border-radius: 0;
}
/* Old .card-box-price — price_additional_text: Karla 14px/400, 14px line,
   5px indent, dark text. */
:host(.ws-tenant-pottersvineyard) .ws-pot-mem-additional {
  margin: 0 0 8px 5px;
  font-family: var(--ws-karla);
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
  color: #1d1d1d;
}
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-card-desc-full {
  font-size: 14px;
  color: #1d1d1d;
  line-height: 1.4;
}
/* Old .card-box-para: Karla 15px/400, 16px line; icon + text spans padded 5px;
   material check_circle at 12px in the text color (dark, not green). */
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-rich-bullets {
  margin: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-rich-bullets li {
  display: flex;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 6px;
  font-family: var(--ws-karla);
  font-size: 15px;
  font-weight: 400;
  line-height: 16px;
  color: #1d1d1d;
}
:host(.ws-tenant-pottersvineyard) .ws-rich-bullets li > span {
  padding: 5px;
}
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-rich-check {
  margin-top: 2px;
  color: #1d1d1d;
}
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-rich-check svg {
  width: 12px;
  height: 12px;
}

/* Old .innerfooter: at least 24px under the content, Learn more centered 10px
   above the price/Join row, the group pinned to the card bottom (Learn more is
   the only auto margin). */
:host(.ws-tenant-pottersvineyard)
  .ws-membership-card
  .ws-rich-inner
  > :has(+ .ws-card-learnmore) {
  margin-bottom: 24px;
}
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-card-learnmore {
  margin: auto auto 10px;
  padding: 8px 12px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 20px;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-accent);
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
}
/* <button>: beat base `#ws-chat-panel button { font-family: var(--ws-font) }`. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-membership-card .ws-card-learnmore {
  font-family: var(--ws-karla);
}
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-learnmore-icon {
  display: inline-flex;
}
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-learnmore-icon svg {
  width: 16px;
  height: 16px;
}
/* Old .innerfooter row: price LEFT, Join CTA RIGHT, min 51px. */
:host(.ws-tenant-pottersvineyard) .ws-pot-mem-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  min-height: 51px;
  font-weight: 600;
}
:host(.ws-tenant-pottersvineyard) .ws-pot-mem-price {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
/* Old .membership-price-details p:first-child: 16px/600 green; p:nth-child(2): 12px black. */
:host(.ws-tenant-pottersvineyard) .ws-pot-mem-price .ws-card-price-amount {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-accent);
  line-height: 1.1;
}
:host(.ws-tenant-pottersvineyard) .ws-pot-mem-price .ws-card-price-unit {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 400;
  color: #000;
}
/* Old .innerfooter a ("Join B-Club"): Karla 14px/600, 9px padding, 1px accent
   border, 2px radius. */
:host(.ws-tenant-pottersvineyard) .ws-fg-mem-cta {
  margin-top: 0;
  flex: 0 0 auto;
  padding: 9px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 2px;
  background: var(--ws-card-accent);
  color: #ffffff;
  font-family: var(--ws-karla);
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}
/* No-cover fallback: padded house logo. */
:host(.ws-tenant-pottersvineyard) .ws-card-img.ws-fg-mem-logo {
  /* aspect-ratio: 16 / 9; */
  object-fit: contain;
  background: #ffffff;
  border-radius: 6px;
}
/* Cover image padding. */
:host(.ws-tenant-pottersvineyard) .ws-membership-card .ws-card-head {
  padding: 6px;
}
:host(.ws-tenant-pottersvineyard)
  .ws-membership-card
  .ws-card-img:not(.ws-fg-mem-logo) {
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 6px;
  background: transparent;
}

/* Guided-flow chips: accent text + border, sized to their text (left-aligned,
   not full-width), tan hover, and a filled "selected" state on click. The
   bolt icon uses currentColor, so it follows the text color automatically. */
/* Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
/* Old prod .option-button box: 8px 12px, 2px radius, 5px icon gap, min 35px
   tall; #button-container stacks chips 5px apart. */
:host(.ws-tenant-pottersvineyard) .ws-prompts {
  gap: 5px;
}
:host(.ws-tenant-pottersvineyard) .ws-prompt-chip {
  gap: 5px;
  min-height: 35px;
  padding: 8px 12px;
  color: var(--ws-card-accent);
  border-color: var(--ws-card-accent);
  border-radius: 2px;
  font-size: 15px;
  font-weight: 500;
  line-height: 16px;
}
/* Base `#ws-chat-panel button { font-family: var(--ws-font) }` is an ID rule,
   so scope through the panel id to put the chip itself on Karla. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-prompt-chip {
  font-family: var(--ws-karla);
}

/* Follow-up recommendation chips — old prod `.option-button.same-opt-btn` in
   `#button-container`: text-only (no bolt) and ONE shared width
   (container max-width 90% + each button width 100%). Guided/welcome chips
   keep their bolt and shrink-to-text. */
:host(.ws-tenant-pottersvineyard) .ws-prompts--recommendations {
  max-width: 90%;
  align-items: stretch;
}
:host(.ws-tenant-pottersvineyard) .ws-prompts--recommendations .ws-prompt-icon {
  display: none;
}
:host(.ws-tenant-pottersvineyard) .ws-prompt-chip:hover {
  background: var(--ws-card-accent);
  color: #fff;
}
:host(.ws-tenant-pottersvineyard) .ws-prompt-chip.is-selected {
  background: var(--ws-card-accent);
  border-color: var(--ws-card-accent);
  color: #ffffff;
}
/* The bolt icon span pins its own color to --ws-primary (ChatPanel.css), which
   overrides the white text on a selected chip and leaves the icon invisible on
   the filled background. Let the icon follow the chip's text color when selected. */
:host(.ws-tenant-pottersvineyard) .ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

/* ── Wine listing card (parity: createWineListing) ────────────────────── */
:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-head {
  position: relative;
  height: 180px;
  min-height: 180px;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-soldout {
  position: absolute;
  right: 8px;
  top: 8px;
  text-align: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  width: fit-content;
  background-color: #000000ab;
  color: #fff;
}

/* Wine title — old prod .wine_card_title: "lithos-pro" 16px/400 (from the
   winery's Adobe kit / theme.font_url; Karla if the kit isn't on the page). */
:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 400;
  font-family: "lithos-pro", var(--ws-karla);
  color: var(--ws-card-accent);
  line-height: normal;
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-meta,
:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-desc {
  margin: 0;
  font-family: var(--ws-card-desc-font);
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  color: #1d1d1d;
  min-height: 46px;
}

:host(.ws-tenant-pottersvineyard) .ws-card-meta,
:host(.ws-tenant-pottersvineyard) .ws-card-desc {
  font-family: var(--ws-card-desc-font);
  font-size: 14px;
  font-weight: 400;
  color: var(--ws-text);
}

:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-body {
  margin-top: 10px;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: auto;
  margin-bottom: 10px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-accent);
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}
/* Old prod .tasting-notes-link renders in the card's Karla. It's a <button>, so
   scope through the panel id to beat base `#ws-chat-panel button`. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-pot-winecard-tasting {
  font-family: var(--ws-karla);
}

:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-product-details {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Footer holds BOTH the price block and the Add to cart / qty-stepper action
   — a row (price left, action right), not stacked, so Add to cart doesn't
   stretch full-width below the price. */
:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-product-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

:host(.ws-tenant-pottersvineyard)
  .ws-pot-winecard-product-footer
  p:nth-child(2),
:host(.ws-tenant-pottersvineyard)
  .ws-pot-winecard-product-footer
  p:nth-child(3) {
  font-size: 0.75rem;
  color: var(--ws-text, #000);
  margin-block: 0;
  font-weight: 400;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-price {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-accent);
}
:host(.ws-tenant-pottersvineyard) .ws-pot-unit {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #000;
}
/* "Add to Cart" — old prod .product-details .add-to-cart: Karla 14px/600,
   9px padding, 1px accent border, 2px radius. */
:host(.ws-tenant-pottersvineyard) .ws-pot-add {
  width: 100%;
  padding: 7px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 2px;
  background: var(--ws-card-accent);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  text-align: center;
  cursor: pointer;
}
/* It's a <button>: base `#ws-chat-panel button { font-family: var(--ws-font) }`
   (Avenir for this tenant) is an ID rule, so scope through the panel id. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-pot-add {
  font-family: var(--ws-karla);
}
/* Old row: .product-footer (width:100%) and the add button (style="width:100%")
   share .product-details ~50/50. Only the add state is halved — the in-cart
   trash/stepper keeps its own width and the price takes the rest. */
:host(.ws-tenant-pottersvineyard) .ws-pot-winecard-purchase-row {
  flex: 1 1 0;
  min-width: 0;
}
:host(.ws-tenant-pottersvineyard)
  .ws-pot-winecard-action-mount:has(> .ws-pot-add) {
  flex: 1 1 0;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-product-action,
:host(.ws-tenant-pottersvineyard) .ws-pot-card .product-action {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-left: auto;
  white-space: nowrap;
  width: auto;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-qty-wrap,
:host(.ws-tenant-pottersvineyard) .ws-pot-card .product-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-card .product-action button {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid #e5e5e5;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-delete svg,
:host(.ws-tenant-pottersvineyard) .ws-pot-card .delete-cart svg {
  width: 16px;
  height: 16px;
  display: block;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-cart-qty,
:host(.ws-tenant-pottersvineyard) .ws-pot-card .cart-qty {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 30px;
  padding: 2px 10px;
  margin: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #0a0a0a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-view-basket {
  border-color: var(--ws-primary);
  color: var(--ws-primary);
  flex: 0 0 auto;
  width: max-content;
}

:host(.ws-tenant-pottersvineyard) .ws-pot-view-basket:hover {
  background: color-mix(in srgb, var(--ws-primary) 8%, transparent);
}

/* Event Card */
:host(.ws-tenant-pottersvineyard) .ws-tasting-card .ws-card-body .ws-card-title,
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-body .ws-card-title {
  font-size: 16px;
  font-weight: 600;
}

/* Tasting card title — old prod .tasting_title: "lithos-pro" 16px/400.
   lithos-pro is NOT bundled: pottersvineyard.com loads it via its own Adobe
   Fonts kit (document-level, so it reaches this shadow root). Anywhere the kit
   isn't on the page (localhost, staging) it falls back to Karla. */
:host(.ws-tenant-pottersvineyard)
  .ws-tasting-card
  .ws-card-body
  .ws-card-title {
  margin: 0 0 8px;
  font-family: "lithos-pro", var(--ws-karla);
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  color: var(--ws-card-accent);
}

/* "Reserve Now" — old prod .tasting-footer a: Karla 14px/600, 9px padding,
   1px accent border, 2px radius. */
:host(.ws-tenant-pottersvineyard) .ws-tasting-footer .ws-card-cta {
  padding: 9px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 2px;
  background: var(--ws-card-accent);
  color: #ffffff;
  font-family: var(--ws-karla);
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

/* ── Event card — old prod .carousel-container3 .event_card ───────────────
   Card: 8px padding, 6px radius, gold glow, Karla. Text sits flush with the
   image, 12px below it. Learn more + the price/CTA row pin to the bottom. */
:host(.ws-tenant-pottersvineyard) .ws-event-card {
  padding: 8px;
  border-radius: 6px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  font-family: var(--ws-karla);
}

:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-head {
  max-height: 180px;
  padding: 0;
  overflow: hidden;
  border-radius: 6px;
}

:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-img {
  object-fit: cover;
  border-radius: 6px;
}

:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-body {
  gap: 0;
  padding: 12px 0 0;
}

/* Old .event_title: "lithos-pro" 16px/400 green (from the winery's Adobe kit /
   theme.font_url; Karla if the kit isn't on the page). */
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-body .ws-card-title {
  margin: 0 0 8px;
  font-family: "lithos-pro", var(--ws-karla);
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  color: var(--ws-card-accent);
}

/* Old .event_date_range (14px/700 black), .event_time (14px/500) and
   .event_location (14px/400, 8px below) — 20px lines. */
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-meta {
  margin: 0;
  font-family: var(--ws-karla);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #1d1d1d;
}
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-meta--date {
  font-weight: 700;
  color: #000;
}
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-meta--time {
  font-weight: 500;
}
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-meta--location {
  margin-bottom: 8px;
}
/* Old .event_description: 12px. */
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-desc {
  margin: 0;
  font-family: var(--ws-karla);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: #1d1d1d;
}

/* Old .event-footer: at least 24px under the details, Learn more centered 10px
   above the CTA row, and the whole group pinned to the card bottom. Only
   Learn more carries margin-top:auto (one auto margin — two would split the
   free space and misalign cards). */
:host(.ws-tenant-pottersvineyard)
  .ws-event-card
  .ws-card-body
  > :has(+ .ws-card-learnmore) {
  margin-bottom: 24px;
}
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-learnmore {
  margin: auto auto 10px;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
}
/* <button>: beat base `#ws-chat-panel button { font-family: var(--ws-font) }`. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-event-card .ws-card-learnmore {
  font-family: var(--ws-karla);
}
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-card-learnmore + .ws-event-footer {
  margin-top: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-event-footer {
  align-items: flex-end;
  font-weight: 600;
}
/* Old row kept min-height 51px when the event has no price. */
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-event-footer:not(:has(.ws-price-block)) {
  min-height: 51px;
}

/* Old .event-footer a ("Check Availability"): Karla 14px/600, 9px padding,
   1px accent border, 2px radius, bottom-right. */
:host(.ws-tenant-pottersvineyard) .ws-event-card .ws-event-footer .ws-card-cta {
  margin-left: auto;
  padding: 9px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 2px;
  background: var(--ws-card-accent);
  color: #ffffff;
  font-family: var(--ws-karla);
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  text-align: center;
}

/* ── Tasting Notes drawer — old prod #winespeak-drawer ────────────────────
   Old container: Karla !important. Its <h2> title and <h4> headings carried no
   widget font-family, so on pottersvineyard.com they took the site's theme
   heading fonts: h2 → "lithos-pro", h4 → "Cardo". Both are loaded by the
   winery page (document-level, so they reach this shadow root there); off-site
   they fall back to Karla / a system serif. */
:host(.ws-tenant-pottersvineyard) {
  /* Drawer theme vars (theme.js only sets these from drawer_* config keys,
     which Potters doesn't have) — keeps the count badge etc. green, not maroon. */
  --ws-drawer-heading: var(--ws-primary);
  --ws-drawer-tab-active: rgb(52, 122, 99);
  --ws-drawer-tab-active-text: #ffffff;
  --ws-drawer-surface: #def5ed;
  --ws-drawer-cta: var(--ws-primary);
}

:host(.ws-tenant-pottersvineyard) .ws-wine-drawer {
  font-family: var(--ws-karla);
}
/* Drawer buttons (tabs, qty, Close, Add to cart) sit under the base
   `#ws-chat-panel button { font-family: var(--ws-font) }` ID rule. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-wine-drawer button {
  font-family: var(--ws-karla);
}

/* Old prod stroked only the collapse arrow (.toggle-collapse svg path). The
   header icon is left alone: the Food Pairings icon is a FILLED path, and an
   added stroke outlined it and made it look heavier than production; the
   Tasting Notes icon already strokes itself with currentColor. */
:host(.ws-tenant-pottersvineyard) .ws-drawer-tab-collapse svg path {
  stroke: var(--ws-primary);
}

:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-header h2,
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-pane h4,
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-regular-price,
:host(.ws-tenant-pottersvineyard) .ws-drawer-fp-heading,
:host(.ws-tenant-pottersvineyard) .ws-drawer-ingredient-bullet,
:host(.ws-tenant-pottersvineyard) .ws-drawer-ingredient-amount,
:host(.ws-tenant-pottersvineyard) .ws-drawer-recipe-back {
  color: var(--ws-primary);
}

/* Old .tasting-notes-header h2: 18px/600 green, in the site's h2 font. */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-header h2 {
  font-family: "lithos-pro", var(--ws-karla);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
}

/* Every drawer <h4> took the site's h4 font (Cardo). */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-pane h4 {
  font-family: "Cardo", Georgia, "Times New Roman", serif;
}
/* Old .tasting-notes-pane h4 (PALATE / REGION / VINEYARD): 13px/700 uppercase.
   Only the plain section headings — food-pairing/recipe h4s keep their own sizes. */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-pane > h4:not([class]) {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

/* Old .tasting-notes-product-title: 14px/700 (badge + price follow the vars above). */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-product-title {
  font-size: 14px;
  font-weight: 700;
}

/* Old .tasting-notes-tab: 12px/600; active tab rgb(52,122,99). */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-tab {
  font-size: 12px;
  font-weight: 600;
}

:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-intro,
:host(.ws-tenant-pottersvineyard) .ws-drawer-pairing-box {
  background: #def5ed;
}

:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-tab.active {
  background: rgb(52, 122, 99);
  color: #fff;
  border-right: 5px solid var(--ws-primary);
}

:host(.ws-tenant-pottersvineyard) .ws-drawer-fp-card {
  border-top: 3px solid var(--ws-primary);
  background: #def5ed;
}
/* Old .food-pairings-card h4: 14px/600 green — on pottersvineyard.com the
   site's h4 rule also gave it Cardo (set above) and a 28px line-height, which
   is the airy two-line title in production. */
:host(.ws-tenant-pottersvineyard) .ws-drawer-fp-card h4 {
  line-height: 28px;
}
/* Old .recipe-refresh ("New Recipes"): no padding/margin, normal spacing, same
   bold weight as the "Recipes" heading it sits in. */
:host(.ws-tenant-pottersvineyard) .ws-drawer-recipe-refresh {
  margin: 0;
  padding: 0;
  letter-spacing: normal;
  font-weight: inherit;
}

/* Drawer footer qty row — old prod .tasting-notes-footer (6px padding) and
   .tasting-notes-qty-control: trash / − / qty / + boxes 28px tall (not 40px),
   1px #E5E5E5, 2px radius. */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-footer {
  padding: 6px;
}
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty button,
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-qty-val {
  height: 28px;
}
/* Old − / + were 12px SVG strokes (1.5px, #0A0A0A), not text glyphs. Hide the
   text character (aria-label still names the button) and draw the exact SVG. */
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-dec,
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-inc {
  font-size: 0;
  line-height: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-dec::before,
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-inc::before {
  content: "";
  display: block;
  width: 12px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-dec::before {
  height: 2px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='2' viewBox='0 0 12 2' fill='none'%3E%3Cpath d='M1 1H11' stroke='%230A0A0A' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}
:host(.ws-tenant-pottersvineyard) .ws-wine-drawer-qty .ws-drawer-inc::before {
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M6 1V11M1 6H11' stroke='%230A0A0A' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}
:host(.ws-tenant-pottersvineyard) .ws-drawer-method-num {
  background: var(--ws-primary);
}

/* ── Order summary (old prod .cart-container / .order-summary-section /
   .order-actions — live pottersvineyard.winespeak.ai index.css). Copy changes
   (volume meta, no deposit row, "Proceed to Checkout") are in
   components/CartPanel.js. ─────────────────────────────────────────────── */

/* No outer card: the old summary sat on the chat background, indented to line
   up with the bot bubble. */
:host(.ws-tenant-pottersvineyard) .ws-cart-panel {
  margin: 0 12px 10px calc(var(--ws-avatar-size) + var(--ws-avatar-gap));
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  font-family: var(--ws-karla);
}

/* Old .cart-product: white line box, 1px rgba(142,142,142,.5), 12px radius. */
:host(.ws-tenant-pottersvineyard) .ws-cart-item {
  align-items: center;
  gap: 12px;
  padding: 8px;
  margin-bottom: 12px;
  border: 1px solid rgba(142, 142, 142, 0.5);
  border-radius: 12px;
  background: #fff;
}
/* Old .product-image: 100px wide, 4:5, contain. */
:host(.ws-tenant-pottersvineyard) .ws-cart-item-img {
  width: 100px;
  height: auto;
  aspect-ratio: 4 / 5;
  object-fit: contain;
}
/* Old .product-body: Open Sans; green 14px/600 title, black 12px meta. */
:host(.ws-tenant-pottersvineyard) .ws-cart-item-body {
  gap: 0;
  font-family: "Open Sans", sans-serif;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ws-primary);
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item-meta {
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: #000;
}
/* Old .product-details: price left / controls right, 24px under the meta. */
:host(.ws-tenant-pottersvineyard) .ws-cart-product-details {
  margin-top: 24px;
  font-weight: 600;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item-price {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1d;
}

/* Old .product-action: trash, then − qty + in .product-quantity-wrapper (4px
   apart, 10px after the trash). 30px gray #F1F1F1 boxes, 2px radius. The
   wrapper's shared `gap: 10px !important` (cards.css) needs !important here. */
:host(.ws-tenant-pottersvineyard) .ws-cart-item-action {
  margin-top: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item .cart-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px !important;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item .cart-quantity-wrapper > button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0;
  border: 1px solid #e5e5e5;
  border-radius: 2px;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #000;
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item .cart-quantity-wrapper > .ws-cp-delete {
  width: 36px;
  margin-right: 6px;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-item .cart-qty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 2px 10px;
  border: 0;
  background: transparent;
  box-shadow: none;
  color: #000;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

/* Old .order-summary-section: an <hr> (1px #E5E5E5 inside the browser's inset
   gray border, 8px margin) above Subtotal and above Estimated total. No
   Bottle Deposit row (CartPanel.js). */
:host(.ws-tenant-pottersvineyard) .ws-cart-summary {
  margin-top: 0;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-summary-row {
  align-items: center;
  margin-top: 8px;
  padding: 8px 0;
  border-top: 1px solid #9a9a9a;
  box-shadow: inset 0 1px 0 #e5e5e5;
  font-size: 15px;
  font-weight: 400;
  color: #0a0a0a;
}
/* Old .order-summary-total: gray 400 label, black 600 amount, 12px margins. */
:host(.ws-tenant-pottersvineyard) .ws-cart-total {
  margin-top: 0;
  padding: 12px 0;
  border-top: 1px solid #9a9a9a;
  font-weight: 400;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-total > span:first-child {
  color: #8e8e8e;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-total > span:last-child {
  color: #000;
  font-weight: 600;
}

/* Old #order-buttons .option-button: outlined green, Karla 15px/500, 8px 12px,
   2px radius, one row 8px apart. */
:host(.ws-tenant-pottersvineyard) .ws-cart-actions {
  flex-wrap: nowrap;
  gap: 8px;
  margin: 0 0 10px;
}
:host(.ws-tenant-pottersvineyard) .ws-cart-action-btn {
  min-height: 35px;
  padding: 8px 12px;
  border: 1px solid var(--ws-primary);
  border-radius: 2px;
  font-size: 15px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
}
/* <button>: beat base `#ws-chat-panel button { font-family: var(--ws-font) }`. */
:host(.ws-tenant-pottersvineyard) #ws-chat-panel .ws-cart-action-btn {
  font-family: var(--ws-karla);
}

:host(.ws-tenant-pottersvineyard) .ws-checkout-chips {
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-top: 14px;
}

/* Shipping form typography and single-column layout from Potter's production. */
:host(.ws-tenant-pottersvineyard) .ws-pv-shipping-form .ws-checkout-section {
  padding: 16px 24px;
  font-family: "Karla", sans-serif;
}

:host(.ws-tenant-pottersvineyard) .ws-pv-shipping-form .ws-checkout-field label {
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  padding-bottom: 7px;
  color: #1d1d1d;
}

:host(.ws-tenant-pottersvineyard) .ws-pv-shipping-form .ws-checkout-section-title {
  font-size: 18px;
  font-weight: 600;
}

:host(.ws-tenant-pottersvineyard) .ws-pv-shipping-form .ws-checkout-input {
  height: 36px;
  font-family: "Karla", sans-serif;
  font-size: 14px;
  line-height: 20px;
  border-radius: 8px;
  color: #000;
}

:host(.ws-tenant-pottersvineyard) .ws-pv-shipping-form .ws-checkout-error:empty {
  display: none;
}

:host(.ws-tenant-pottersvineyard) .ws-checkout-chips .ws-cart-action-btn {
  color: var(--ws-primary);
  background: #fff;
  letter-spacing: 0.5px;
}

:host(.ws-tenant-pottersvineyard) .ws-checkout-chips .ws-cart-action-btn.is-selected {
  background: var(--ws-primary);
  color: #fff;
}

/* ── "Updating your cart..." toast on Proceed to Checkout (checkoutRefresh.js)
   Old prod #toast-section + .toast: a modal-style backdrop that dims the whole
   chat (rgba(0,0,0,.51)) with the white toast on top. */
:host(.ws-tenant-pottersvineyard) .ws-pv-cart-overlay {
  position: absolute;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.51);
  border-radius: inherit;
}
:host(.ws-tenant-pottersvineyard) .ws-pv-cart-toast {
  position: absolute;
  left: 50%;
  bottom: 20%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 80%;
  box-sizing: border-box;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px -1px rgba(0, 0, 0, 0.1);
  font-family: var(--ws-karla);
  text-align: left;
}
:host(.ws-tenant-pottersvineyard) .ws-pv-cart-toast-icon {
  display: flex;
  flex-shrink: 0;
  color: var(--ws-primary);
}
:host(.ws-tenant-pottersvineyard) .ws-pv-cart-toast strong {
  display: block;
  color: var(--ws-primary);
  font-size: 15px;
  font-weight: 700;
}
:host(.ws-tenant-pottersvineyard) .ws-pv-cart-toast p {
  margin: 2px 0 0;
  color: rgb(76, 58, 39);
  font-size: 13px;
}
